import React, { useState, useEffect } from 'react';
import {
  adminGetAllAgreements,
  adminUpdateDeliveryStatus,
  adminGetAllLandlords,
  adminGetStatistics,
  adminDownloadAgreementPDF
} from '../services/adminApi';

export default function AdminDashboard({ admin, onLogout }) {
  const [view, setView] = useState('agreements');
  const [agreements, setAgreements] = useState([]);
  const [landlords, setLandlords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ delivery_status: '', tracking_number: '', courier_company: '' });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [view, statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (view === 'agreements') {
        const data = await adminGetAllAgreements({ status_filter: statusFilter, limit: 200 });
        setAgreements(data.agreements || []);
      } else if (view === 'landlords') {
        const data = await adminGetAllLandlords({ search: searchTerm, limit: 200 });
        setLandlords(data.landlords || []);
      } else if (view === 'stats') {
        const data = await adminGetStatistics();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (agr) => {
    setEditingId(agr.id);
    setEditForm({
      delivery_status: agr.delivery_status,
      tracking_number: agr.tracking_number || '',
      courier_company: agr.courier_company || ''
    });
  };

  const saveEdit = async (id) => {
    setUpdating(true);
    try {
      await adminUpdateDeliveryStatus(id, editForm);
      setEditingId(null);
      loadData();
      alert('✅ Status updated successfully!');
    } catch (error) {
      alert('❌ ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const downloadPDF = async (agr) => {
    try {
      await adminDownloadAgreementPDF(agr.id, agr.tenant_name);
    } catch (error) {
      alert('Download failed: ' + error.message);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-';

  return (
    <div className="adm-root">
      <style>{STYLES}</style>

      {/* Sidebar */}
      <aside className="adm-sidebar">
        <div className="adm-logo-wrap">
          <img src="/Hashvi_logo_white_no_BG.svg" alt="Hashvi" style={{ height: 60, marginBottom: 8 }} />
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#f26522', background: 'rgba(242,101,34,0.15)', padding: '3px 10px', borderRadius: 8 }}>ADMIN</div>
        </div>

        <div className="adm-nav">
          <button className={`adm-nav-btn ${view === 'agreements' ? 'active' : ''}`} onClick={() => setView('agreements')}>
            📋 Agreement Orders
            {agreements.length > 0 && <span className="adm-badge">{agreements.length}</span>}
          </button>
          <button className={`adm-nav-btn ${view === 'landlords' ? 'active' : ''}`} onClick={() => setView('landlords')}>
            👥 All Landlords
          </button>
          <button className={`adm-nav-btn ${view === 'stats' ? 'active' : ''}`} onClick={() => setView('stats')}>
            📊 Statistics
          </button>
        </div>

        <div className="adm-user">
          <div className="adm-avatar">{admin.name ? admin.name[0] : 'A'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.9)' }}>{admin.name || admin.username}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{admin.role}</div>
          </div>
          <button className="adm-logout" onClick={onLogout}>Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="adm-main">
        
        <div className="adm-header">
          <div>
            <h1 className="adm-title">
              {view === 'agreements' ? 'Agreement Orders' : view === 'landlords' ? 'All Landlords' : 'System Statistics'}
            </h1>
            <p className="adm-sub">
              {view === 'agreements' ? 'Manage delivery status for all orders' : view === 'landlords' ? 'View all registered landlords' : 'Overall system metrics'}
            </p>
          </div>
        </div>

        {/* AGREEMENTS VIEW */}
        {view === 'agreements' && (
          <>
            <div className="adm-filters">
              <button className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>All</button>
              <button className={`filter-btn ${statusFilter === 'order_placed' ? 'active' : ''}`} onClick={() => setStatusFilter('order_placed')}>Order Placed</button>
              <button className={`filter-btn ${statusFilter === 'printed' ? 'active' : ''}`} onClick={() => setStatusFilter('printed')}>Printed</button>
              <button className={`filter-btn ${statusFilter === 'shipped' ? 'active' : ''}`} onClick={() => setStatusFilter('shipped')}>Shipped</button>
              <button className={`filter-btn ${statusFilter === 'delivered' ? 'active' : ''}`} onClick={() => setStatusFilter('delivered')}>Delivered</button>
            </div>

            {loading ? (
              <div className="adm-loading">Loading agreements...</div>
            ) : agreements.length === 0 ? (
              <div className="adm-empty">No agreements found</div>
            ) : (
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Landlord</th>
                      <th>Tenant</th>
                      <th>Property</th>
                      <th>Ordered</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Tracking</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreements.map(agr => {
                      const isEditing = editingId === agr.id;
                      return (
                        <tr key={agr.id} className={isEditing ? 'editing' : ''}>
                          <td>#{agr.id}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{agr.landlord_name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{agr.landlord_phone}</div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{agr.tenant_name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{agr.tenant_phone}</div>
                          </td>
                          <td>
                            <div>{agr.building_name}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>{agr.unit_number}</div>
                          </td>
                          <td>{fmt(agr.ordered_at || agr.paid_at)}</td>
                          <td>
                            <span className={`pay-badge ${agr.payment_status}`}>
                              {agr.payment_status === 'success' ? '✓ Paid' : 'Pending'}
                            </span>
                          </td>
                          <td>
                            {!isEditing ? (
                              <span className={`status-badge ${agr.delivery_status}`}>
                                {agr.delivery_status.replace('_', ' ')}
                              </span>
                            ) : (
                              <select 
                                className="status-select"
                                value={editForm.delivery_status}
                                onChange={(e) => setEditForm({...editForm, delivery_status: e.target.value})}
                              >
                                <option value="order_placed">Order Placed</option>
                                <option value="printed">Printed</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                              </select>
                            )}
                          </td>
                          <td>
                            {!isEditing ? (
                              agr.tracking_number ? (
                                <div>
                                  <div style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 600 }}>{agr.tracking_number}</div>
                                  <div style={{ fontSize: 10, color: '#64748b' }}>{agr.courier_company}</div>
                                </div>
                              ) : (
                                <span style={{ fontSize: 11, color: '#94a3b8' }}>Not set</span>
                              )
                            ) : (
                              <div>
                                <input
                                  type="text"
                                  placeholder="Tracking number"
                                  className="track-input"
                                  value={editForm.tracking_number}
                                  onChange={(e) => setEditForm({...editForm, tracking_number: e.target.value})}
                                />
                                <select
                                  className="courier-select"
                                  value={editForm.courier_company}
                                  onChange={(e) => setEditForm({...editForm, courier_company: e.target.value})}
                                >
                                  <option value="">Select courier</option>
                                  <option value="Delhivery">Delhivery</option>
                                  <option value="BlueDart">BlueDart</option>
                                  <option value="DTDC">DTDC</option>
                                  <option value="IndiaPost">India Post</option>
                                </select>
                              </div>
                            )}
                          </td>
                          <td>
                            {!isEditing ? (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="action-btn edit" onClick={() => startEdit(agr)} disabled={agr.payment_status !== 'success'}>
                                  Edit
                                </button>
                                {agr.pdf_url && (
                                  <button className="action-btn download" onClick={() => downloadPDF(agr)}>
                                    PDF
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button className="action-btn save" onClick={() => saveEdit(agr.id)} disabled={updating}>
                                  {updating ? 'Saving...' : 'Save'}
                                </button>
                                <button className="action-btn cancel" onClick={() => setEditingId(null)}>
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* LANDLORDS VIEW */}
        {view === 'landlords' && (
          <>
            <div className="adm-search">
              <input
                type="text"
                placeholder="Search by name, phone, email..."
                className="search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && loadData()}
              />
              <button className="search-btn" onClick={loadData}>Search</button>
            </div>

            {loading ? (
              <div className="adm-loading">Loading landlords...</div>
            ) : (
              <div className="landlords-grid">
                {landlords.map(ll => (
                  <div key={ll.id} className="landlord-card">
                    <div className="landlord-header">
                      <div>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1f4e79' }}>{ll.name}</h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: 13, color: '#64748b' }}>{ll.phone}</p>
                        {ll.email && <p style={{ margin: '2px 0 0 0', fontSize: 12, color: '#64748b' }}>{ll.email}</p>}
                      </div>
                      <div className={`approval-badge ${ll.is_approved ? 'approved' : 'pending'}`}>
                        {ll.is_approved ? '✓ Active' : 'Pending'}
                      </div>
                    </div>
                    <div className="landlord-stats">
                      <div className="stat-item">
                        <div className="stat-value">{ll.stats.buildings}</div>
                        <div className="stat-label">Buildings</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{ll.stats.active_tenants}</div>
                        <div className="stat-label">Tenants</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{ll.stats.agreements}</div>
                        <div className="stat-label">Agreements</div>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 12 }}>
                      Joined: {new Date(ll.created_at).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* STATISTICS VIEW */}
        {view === 'stats' && (
          loading ? (
            <div className="adm-loading">Loading statistics...</div>
          ) : stats && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">👥</div>
                  <div className="stat-number">{stats.total_landlords}</div>
                  <div className="stat-title">Total Landlords</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🏢</div>
                  <div className="stat-number">{stats.total_buildings}</div>
                  <div className="stat-title">Total Buildings</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">👤</div>
                  <div className="stat-number">{stats.total_active_tenants}</div>
                  <div className="stat-title">Active Tenants</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📋</div>
                  <div className="stat-number">{stats.total_agreements}</div>
                  <div className="stat-title">Paid Agreements</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">💰</div>
                  <div className="stat-number">₹{(stats.total_revenue || 0).toLocaleString()}</div>
                  <div className="stat-title">Total Revenue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-number">{stats.recent_agreements_7days}</div>
                  <div className="stat-title">Last 7 Days</div>
                </div>
              </div>

              <div className="delivery-status-chart">
                <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1f4e79', margin: '0 0 20px 0' }}>Delivery Status Breakdown</h3>
                <div className="status-bars">
                  {Object.entries(stats.delivery_status || {}).map(([status, count]) => (
                    <div key={status} className="status-bar-row">
                      <div className="status-bar-label">{status.replace('_', ' ')}</div>
                      <div className="status-bar-track">
                        <div 
                          className={`status-bar-fill ${status}`}
                          style={{ width: `${stats.total_agreements > 0 ? (count / stats.total_agreements) * 100 : 0}%` }}
                        />
                      </div>
                      <div className="status-bar-count">{count}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )
        )}

      </main>
    </div>
  );
}

const STYLES = `
  .adm-root { display: flex; min-height: 100vh; font-family: 'Inter', sans-serif; background: #f1f5f9; }
  
  .adm-sidebar {
    width: 260px; background: #1f4e79; display: flex; flex-direction: column;
    position: fixed; top: 0; left: 0; bottom: 0; overflow-y: auto;
  }
  .adm-logo-wrap { padding: 32px 20px 24px; border-bottom: 1px solid rgba(255,255,255,0.1); text-align: center; }
  .adm-nav { flex: 1; padding: 20px 12px; }
  .adm-nav-btn {
    width: 100%; padding: 12px 16px; margin-bottom: 6px; background: none;
    border: none; border-radius: 8px; color: rgba(255,255,255,0.7); font-size: 14px;
    text-align: left; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 8px;
  }
  .adm-nav-btn:hover { background: rgba(255,255,255,0.08); color: white; }
  .adm-nav-btn.active { background: rgba(242,101,34,0.2); color: white; border: 1px solid rgba(242,101,34,0.3); }
  .adm-badge {
    margin-left: auto; background: #f26522; color: white; padding: 2px 8px;
    border-radius: 10px; font-size: 11px; font-weight: 600;
  }
  .adm-user {
    padding: 16px; border-top: 1px solid rgba(255,255,255,0.1);
    display: flex; align-items: center; gap: 12px;
  }
  .adm-avatar {
    width: 36px; height: 36px; border-radius: 50%; background: #f26522;
    display: flex; align-items: center; justify-content: center;
    font-weight: 600; color: white; flex-shrink: 0;
  }
  .adm-logout {
    padding: 6px 12px; background: none; border: 1px solid rgba(255,255,255,0.2);
    border-radius: 6px; color: rgba(255,255,255,0.7); font-size: 11px;
    cursor: pointer; transition: all 0.2s;
  }
  .adm-logout:hover { background: rgba(255,255,255,0.1); color: white; }
  
  .adm-main { margin-left: 260px; flex: 1; padding: 24px; }
  .adm-header { margin-bottom: 24px; }
  .adm-title { font-size: 24px; font-weight: 700; color: #1f4e79; margin: 0 0 4px 0; }
  .adm-sub { font-size: 14px; color: #64748b; margin: 0; }
  
  .adm-filters {
    display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap;
  }
  .filter-btn {
    padding: 8px 16px; background: white; border: 1px solid #e2e8f0;
    border-radius: 8px; font-size: 13px; font-weight: 500; color: #64748b;
    cursor: pointer; transition: all 0.2s;
  }
  .filter-btn:hover { border-color: #f26522; color: #f26522; }
  .filter-btn.active { background: #f26522; color: white; border-color: #f26522; }
  
  .adm-loading, .adm-empty {
    text-align: center; padding: 60px 20px; color: #64748b; font-size: 15px;
  }
  
  .adm-table-wrap {
    background: white; border-radius: 12px; border: 1px solid #e2e8f0;
    overflow-x: auto; box-shadow: 0 2px 8px rgba(31,78,121,0.06);
  }
  .adm-table { width: 100%; border-collapse: collapse; }
  .adm-table thead { background: #f8fafc; }
  .adm-table th {
    padding: 12px 16px; text-align: left; font-size: 11px; font-weight: 600;
    color: #64748b; text-transform: uppercase; letter-spacing: 0.5px;
    border-bottom: 2px solid #e2e8f0;
  }
  .adm-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.2s; }
  .adm-table tbody tr:hover { background: #f8fafc; }
  .adm-table tbody tr.editing { background: #fff7ed; }
  .adm-table td { padding: 14px 16px; font-size: 13px; color: #1e293b; }
  
  .pay-badge, .status-badge {
    display: inline-block; padding: 4px 10px; border-radius: 12px;
    font-size: 11px; font-weight: 600;
  }
  .pay-badge.success { background: #d1fae5; color: #065f46; }
  .pay-badge.created, .pay-badge.failed { background: #fee2e2; color: #991b1b; }
  
  .status-badge { text-transform: capitalize; }
  .status-badge.order_placed { background: #dbeafe; color: #1e40af; }
  .status-badge.printed { background: #fef3c7; color: #92400e; }
  .status-badge.shipped { background: #e9d5ff; color: #6b21a8; }
  .status-badge.delivered { background: #d1fae5; color: #065f46; }
  
  .status-select, .track-input, .courier-select {
    padding: 6px 10px; font-size: 12px; border: 1px solid #e2e8f0;
    border-radius: 6px; width: 100%;
  }
  .track-input { margin-bottom: 6px; }
  
  .action-btn {
    padding: 6px 12px; font-size: 11px; font-weight: 600; border: none;
    border-radius: 6px; cursor: pointer; transition: all 0.2s;
  }
  .action-btn.edit { background: #1f4e79; color: white; }
  .action-btn.edit:hover { background: #163a5c; }
  .action-btn.edit:disabled { background: #cbd5e1; cursor: not-allowed; }
  .action-btn.download { background: #f26522; color: white; }
  .action-btn.download:hover { background: #d94e0d; }
  .action-btn.save { background: #10b981; color: white; }
  .action-btn.save:hover { background: #059669; }
  .action-btn.cancel { background: #ef4444; color: white; }
  .action-btn.cancel:hover { background: #dc2626; }
  
  .adm-search {
    display: flex; gap: 12px; margin-bottom: 20px;
  }
  .search-input {
    flex: 1; padding: 12px 16px; font-size: 14px; border: 1px solid #e2e8f0;
    border-radius: 8px; transition: all 0.2s;
  }
  .search-input:focus {
    outline: none; border-color: #f26522;
    box-shadow: 0 0 0 3px rgba(242,101,34,0.1);
  }
  .search-btn {
    padding: 12px 24px; background: #f26522; color: white; border: none;
    border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s;
  }
  .search-btn:hover { background: #d94e0d; }
  
  .landlords-grid {
    display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
  }
  .landlord-card {
    background: white; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: 20px; box-shadow: 0 2px 8px rgba(31,78,121,0.06);
    transition: box-shadow 0.2s;
  }
  .landlord-card:hover { box-shadow: 0 4px 16px rgba(31,78,121,0.12); }
  .landlord-header {
    display: flex; justify-content: space-between; align-items: flex-start;
    margin-bottom: 16px; padding-bottom: 16px; border-bottom: 1px solid #f1f5f9;
  }
  .approval-badge {
    padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;
  }
  .approval-badge.approved { background: #d1fae5; color: #065f46; }
  .approval-badge.pending { background: #fee2e2; color: #991b1b; }
  .landlord-stats {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  }
  .stat-item { text-align: center; }
  .stat-value { font-size: 24px; font-weight: 700; color: #1f4e79; }
  .stat-label { font-size: 11px; color: #64748b; margin-top: 4px; }
  
  .stats-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px; margin-bottom: 32px;
  }
  .stat-card {
    background: white; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: 24px; text-align: center; box-shadow: 0 2px 8px rgba(31,78,121,0.06);
  }
  .stat-icon { font-size: 32px; margin-bottom: 12px; }
  .stat-number { font-size: 28px; font-weight: 700; color: #1f4e79; margin-bottom: 4px; }
  .stat-title { font-size: 13px; color: #64748b; font-weight: 500; }
  
  .delivery-status-chart {
    background: white; border: 1px solid #e2e8f0; border-radius: 12px;
    padding: 24px; box-shadow: 0 2px 8px rgba(31,78,121,0.06);
  }
  .status-bars { display: flex; flex-direction: column; gap: 16px; }
  .status-bar-row {
    display: grid; grid-template-columns: 140px 1fr 60px; gap: 12px; align-items: center;
  }
  .status-bar-label {
    font-size: 13px; font-weight: 500; color: #64748b; text-transform: capitalize;
  }
  .status-bar-track {
    height: 32px; background: #f1f5f9; border-radius: 8px; overflow: hidden; position: relative;
  }
  .status-bar-fill {
    height: 100%; transition: width 0.5s ease; position: absolute; top: 0; left: 0;
  }
  .status-bar-fill.order_placed { background: #3b82f6; }
  .status-bar-fill.printed { background: #f59e0b; }
  .status-bar-fill.shipped { background: #8b5cf6; }
  .status-bar-fill.delivered { background: #10b981; }
  .status-bar-count {
    font-size: 16px; font-weight: 700; color: #1f4e79; text-align: right;
  }
  
  @media (max-width: 768px) {
    .adm-sidebar { display: none; }
    .adm-main { margin-left: 0; }
    .stats-grid { grid-template-columns: repeat(2, 1fr); }
    .landlords-grid { grid-template-columns: 1fr; }
  }
`;